import React, { FC, useRef, useEffect, useState } from 'react';
import { graphql } from 'gatsby';

import {
  StyledSection,
  StyledContentBlock,
  MainContainer,
  MainContentWrapper,
  Title,
  Text,
  ContentContainer,
  ImageSection,
  Image,
  Video,
  ContentSection,
  LoaderContainer,
} from './styled';

import WidgetV2 from '../WidgetV2';
import SignInWidget from '../SignInWidget';
import LoaderDots from '../Loader/LoaderDots';

interface AddressMap {
  BTC: string;
  BCH: string;
  ETH: string;
  SOL: string;
  POL: string;
  BSC: string;
  DOGE: string;
  XRP: string;
  AVAX: string;
  [key: string]: string;
}

interface StytchUser {
  user_id: string;
  emails: Array<{ email: string; email_id: string; verified: boolean }>;
  phone_numbers: Array<{ phone_number: string }>;
  name?: {
    first_name?: string;
    last_name?: string;
  };
}

interface Props {
  crypto?: string;
  provider?: string;
  showLoginWidget?: boolean;
  isLoading: boolean;
  hasAccount: boolean;
  user: StytchUser | null;
  showVideo: boolean;
}

const HeaderHome: FC<Props & Queries.SeoHeaderHomeFragment> = ({
  headerHome,
  crypto,
  provider,
  showLoginWidget,
  isLoading,
  hasAccount,
  user,
  showVideo,
}) => {
  const { title, subtitle, image } = headerHome || {};
  const videoRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState('/images/buy-sell-bitcoin.webm');
  const [storedAddresses, setStoredAddresses] = useState<AddressMap | null>(
    null,
  );
  const [addressCheckTimedOut, setAddressCheckTimedOut] = useState(false);

  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      setVideoSrc('/images/buy-sell-bitcoin.mp4');
    } else {
      setVideoSrc('/images/buy-sell-bitcoin.webm');
    }
  }, []);

  function renderWidget() {
    if (hasAccount && !addressCheckTimedOut) {
      if (
        isLoading ||
        storedAddresses === null ||
        Object.keys(storedAddresses).length === 0
      ) {
        return (
          <LoaderContainer>
            <LoaderDots />
          </LoaderContainer>
        );
      }
    }

    if (showLoginWidget && hasAccount) {
      return (
        <SignInWidget crypto={crypto} addresses={storedAddresses} user={user} />
      );
    }

    return <WidgetV2 crypto={crypto} provider={provider} />;
  }

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>;
    let timeoutId: ReturnType<typeof setTimeout>;

    const checkMPCAddresses = () => {
      const raw = localStorage.getItem('addresses');
      let parsed: AddressMap | null = null;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch (err) {
          console.warn('Could not parse stored addresses:', raw, err);
          localStorage.removeItem('addresses');
        }
      }

      setStoredAddresses(parsed);

      if (raw && parsed && Object.keys(parsed).length > 0) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      }
    };

    checkMPCAddresses();
    intervalId = setInterval(checkMPCAddresses, 500);

    // Stop polling after 10 seconds to prevent infinite loading
    timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      setAddressCheckTimedOut(true);
    }, 10000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <StyledSection>
      <StyledContentBlock>
        <MainContainer>
          <MainContentWrapper>
            <Title>{title}</Title>
            <Text>{subtitle}</Text>
          </MainContentWrapper>
          <ContentContainer>
            <ImageSection>
              {/* <Image
                src={image?.src ?? undefined}
                alt={image?.alt ?? undefined}
                width={550}
                height={744}
                loading="eager"
                // @ts-expect-error fetchPriority is a valid HTML attribute but not yet in React types
                fetchpriority="high"
                $hidden={showVideo}
              /> */}
              <Video
                ref={videoRef}
                autoPlay
                preload="auto"
                loop
                muted
                playsInline
                disablePictureInPicture
                $hidden={!showVideo}
                width={650}
                height={650}
              >
                {videoSrc && (
                  <source
                    src={videoSrc}
                    type={
                      videoSrc.endsWith('.webm') ? 'video/webm' : 'video/mp4'
                    }
                  />
                )}
                <Image
                src={image?.src ?? undefined}
                alt={image?.alt ?? undefined}
                width={550}
                height={744}
                loading="eager"
                // @ts-expect-error fetchPriority is a valid HTML attribute but not yet in React types
                fetchpriority="high"
                $hidden={showVideo}
              />
              </Video>
            </ImageSection>
            <ContentSection>{renderWidget()}</ContentSection>
          </ContentContainer>
        </MainContainer>
      </StyledContentBlock>
    </StyledSection>
  );
};

export const query = graphql`
  fragment SeoHeaderHome on Json {
    headerHome {
      title
      subtitle
      image {
        src
        alt
      }
      widgetSkeletonContent {
        title
        buttonText
      }
    }
  }
`;

export default HeaderHome;
